import get from 'lodash/get';
import set from 'lodash/set';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateComponent } from '~/app/components/dynamic-grid/dynamic-grid-helpers';
import { useBuilder } from '~/app/lib/redux/selectors/builder-selector';
import { useDynamicGrid } from '~/app/lib/redux/selectors/dynamic-grid-selector';

type SpacingsOptions = {
  paddingTop?: number;
  paddingRight?: number;
  paddingBottom?: number;
  paddingLeft?: number;
  paddingUnit?: string;
  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
  marginLeft?: number;
  marginUnit?: string;
};

export type SpacingsType = SpacingsOptions & {
  tablet?: SpacingsOptions;
  mobile?: SpacingsOptions;
};

const initialSpacings: SpacingsType = {
  paddingTop: 0,
  paddingRight: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingUnit: 'px',
  marginTop: 0,
  marginRight: 0,
  marginBottom: 0,
  marginLeft: 0,
  marginUnit: 'px',
};

export const useDynamicAccordion = () => {
  const dispatch = useDispatch();
  const { componentPath } = useDynamicGrid();
  const { flatOutline, selected: selectedComponentId } = useBuilder();
  const currentComponent = flatOutline?.[selectedComponentId];

  const [spacings, setSpacings] = useState<SpacingsType>(initialSpacings);

  const getSections = useCallback(() => {
    const sections = get(
      currentComponent,
      `props.${componentPath}.sections`,
      []
    );
    return sections;
  }, [currentComponent, componentPath]);

  const handleBodyContentTypeChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
    index: number
  ) => {
    const sections = getSections();
    const updatedSections = [...sections];
    updatedSections[index] = {
      ...updatedSections[index],
      bodyContentType: e.target.value,
    };
    set(currentComponent, `props.${componentPath}.sections`, updatedSections);
    dispatch(updateComponent(currentComponent));
  };

  const handleSpacingsChange = (
    fieldPath: string,
    fieldName: string,
    e: string | number
  ) => {
    setSpacings((prevState) => ({
      ...prevState,
      [fieldName]: e,
    }));

    const fieldPathValue = fieldPath
      ? `props.${componentPath}.${fieldPath}.spacings` // If fieldPath is provided, update the nested field
      : `props.${componentPath}.spacings`; // If fieldPath is not provided, update the top-level field

    set(currentComponent, fieldPathValue, {
      ...spacings,
      [fieldName]: e,
    });
    dispatch(updateComponent(currentComponent));
  };

  const handleTabletSpacingsChange = (
    fieldPath: string,
    fieldName: string,
    e: string | number
  ) => {
    setSpacings((prevState) => ({
      ...prevState,
      tablet: { [fieldName]: e },
    }));

    const fieldPathValue = fieldPath
      ? `props.${componentPath}.${fieldPath}.spacings` // If fieldPath is provided, update the nested field
      : `props.${componentPath}.spacings`; // If fieldPath is not provided, update the top-level field

    set(currentComponent, fieldPathValue, {
      ...spacings,
      tablet: { [fieldName]: e },
    });
    dispatch(updateComponent(currentComponent));
  };

  const handleMobileSpacingsChange = (
    fieldPath: string,
    fieldName: string,
    e: string | number
  ) => {
    setSpacings((prevState) => ({
      ...prevState,
      mobile: { [fieldName]: e },
    }));

    const fieldPathValue = fieldPath
      ? `props.${componentPath}.${fieldPath}.spacings` // If fieldPath is provided, update the nested field
      : `props.${componentPath}.spacings`; // If fieldPath is not provided, update the top-level field

    set(currentComponent, fieldPathValue, {
      ...spacings,
      mobile: { [fieldName]: e },
    });
    dispatch(updateComponent(currentComponent));
  };

  const addSection = useCallback(() => {
    const sections = getSections();
    const newSection = {
      bodyContentType: 'text',
      header: { text: `This is section ${sections.length + 1}` },
      body: { text: `This is section ${sections.length + 1} body` },
    };
    const updatedSections = [...sections, newSection];
    set(currentComponent, `props.${componentPath}.sections`, updatedSections);
    dispatch(updateComponent(currentComponent));
  }, [getSections, currentComponent, componentPath, dispatch]);

  const deleteSection = useCallback(
    (index: number) => {
      const sections = getSections();
      const updatedSections = sections.filter(
        (_: any, i: number) => i !== index
      );
      set(currentComponent, `props.${componentPath}.sections`, updatedSections);
      dispatch(updateComponent(currentComponent));
    },
    [getSections, currentComponent, componentPath, dispatch]
  );

  return {
    handleBodyContentTypeChange,
    handleSpacingsChange,
    handleTabletSpacingsChange,
    handleMobileSpacingsChange,
    getSections,
    addSection,
    deleteSection,
  };
};
